import { FC } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { CheckCircle2, CircleX, Clock3 } from 'lucide-react';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '../../../components/ui/Card';
import { Routes } from '../../../models/routes';
import {
    ActivePremiumService,
    PaymentResultInvoice,
    PaymentResultPayment,
} from '../../../types';
import { formatLocalDateTime } from '../../../utils';

type PageProps = {
    invoice: PaymentResultInvoice;
    payment: PaymentResultPayment | null;
    serviceActivation: ActivePremiumService | null;
};

const resolveStatusTone = (status: string) => {
    switch (status) {
        case 'paid':
        case 'success':
        case 'active':
            return {
                icon: CheckCircle2,
                badgeVariant: 'success' as const,
                title: 'Оплата успішна',
            };
        case 'failed':
        case 'failure':
        case 'expired':
            return {
                icon: CircleX,
                badgeVariant: 'destructive' as const,
                title: 'Оплата не завершена',
            };
        default:
            return {
                icon: Clock3,
                badgeVariant: 'warning' as const,
                title: 'Очікуємо статус оплати',
            };
    }
};

const invoiceStatusLabel = (status: string) => {
    switch (status) {
        case 'paid':
            return 'Оплачено';
        case 'failed':
            return 'Не оплачено';
        case 'expired':
            return 'Прострочено';
        case 'cancelled':
            return 'Скасовано';
        case 'payment_page_created':
            return 'Очікує оплату';
        case 'pending':
            return 'Очікує';
        default:
            return status;
    }
};

const PremiumPaymentResult: FC = () => {
    const { invoice, payment, serviceActivation } = usePage<PageProps>().props;
    const tone = resolveStatusTone(invoice.status);
    const StatusIcon = tone.icon;

    return (
        <div className="py-8">
            <Card className="mx-auto max-w-3xl">
                <CardHeader className="pb-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <CardTitle className="flex items-center gap-2 text-2xl">
                                <StatusIcon className="h-6 w-6" />
                                {tone.title}
                            </CardTitle>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Рахунок {invoice.invoice_number} для
                                premium-пакета {invoice.service_name}
                            </p>
                        </div>
                        <Badge variant={tone.badgeVariant}>
                            {invoiceStatusLabel(invoice.status)}
                        </Badge>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl border p-4">
                            <h2 className="text-sm font-semibold">Інвойс</h2>
                            <div className="mt-3 space-y-2 text-sm">
                                <div className="flex justify-between gap-3">
                                    <span className="text-muted-foreground">
                                        Сума
                                    </span>
                                    <span className="font-medium">
                                        ₴{(invoice.amount / 100).toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <span className="text-muted-foreground">
                                        Створено
                                    </span>
                                    <span>
                                        {formatLocalDateTime(
                                            invoice.created_at,
                                        )}
                                    </span>
                                </div>
                                {invoice.paid_at && (
                                    <div className="flex justify-between gap-3">
                                        <span className="text-muted-foreground">
                                            Оплачено
                                        </span>
                                        <span>
                                            {formatLocalDateTime(
                                                invoice.paid_at,
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-xl border p-4">
                            <h2 className="text-sm font-semibold">Платіж</h2>
                            <div className="mt-3 space-y-2 text-sm">
                                <div className="flex justify-between gap-3">
                                    <span className="text-muted-foreground">
                                        Статус Monobank
                                    </span>
                                    <span>
                                        {payment?.provider_status || '—'}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <span className="text-muted-foreground">
                                        Спосіб
                                    </span>
                                    <span>
                                        {payment?.payment_method || '—'}
                                    </span>
                                </div>
                                {payment?.failure_reason && (
                                    <div className="text-destructive">
                                        {payment.failure_reason}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {serviceActivation && (
                        <div className="rounded-xl border p-4">
                            <h2 className="text-sm font-semibold">
                                Активований пакет
                            </h2>
                            <div className="mt-3 flex flex-wrap gap-2 text-sm">
                                <Badge variant="outline">
                                    Всього:{' '}
                                    {serviceActivation.premium_ads_count}
                                </Badge>
                                <Badge variant="success">
                                    Доступно:{' '}
                                    {serviceActivation.available_slots_count}
                                </Badge>
                                <Badge variant="info">
                                    Активно:{' '}
                                    {serviceActivation.active_slots_count}
                                </Badge>
                                <Badge variant="secondary">
                                    Використано:{' '}
                                    {serviceActivation.used_slots_count}
                                </Badge>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                        <Button asChild>
                            <Link href={Routes.Offers}>
                                Повернутись до пропозицій
                            </Link>
                        </Button>
                        {invoice.provider_page_url &&
                            invoice.status === 'payment_page_created' && (
                                <Button variant="outline" asChild>
                                    <a href={invoice.provider_page_url}>
                                        Спробувати оплатити ще раз
                                    </a>
                                </Button>
                            )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default PremiumPaymentResult;
